<script lang="ts" setup>
import { InputAutoCompletion } from '@sancare/typescript-commons'
import type { CompletionOption } from '@sancare/typescript-commons/src/components/types'
import { UseMapToCompletionOptions } from '@sancare/typescript-commons/src/composables/useMapToCompletionOptions'
import {
  useAutoCompletionDebouncedWatcher
} from '@sancare/typescript-commons/src/misc/composables/useAutoCompletionDebouncedWatcher'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useStore } from '@/store'
import { actionLabels, AutomationActionEnum } from '@/store/modules/automation/types'
import { Diagnosis } from '@/store/modules/diagnosis/types'

const props = defineProps<{
  showCopyActions: boolean
}>()

const store = useStore()
const { t } = useI18n()
const emit = defineEmits(['save-action'])

const typedInput = ref('')
const showCompletion = ref(false)
const actionAction = ref<AutomationActionEnum>(AutomationActionEnum.ADD_DP)
const actionValue = ref('')

const COPY_ACTIONS = [AutomationActionEnum.COPY_DP, AutomationActionEnum.COPY_DR, AutomationActionEnum.COPY_DAS]

const availableActions = computed<AutomationActionEnum[]>(() =>
  (Object.values(AutomationActionEnum) as AutomationActionEnum[]).filter(
    (action) => !COPY_ACTIONS.includes(action) || props.showCopyActions
  )
)

const isCopyAction = computed(() => COPY_ACTIONS.includes(actionAction.value))

const completionItems = computed<CompletionOption<Diagnosis>[]>(() => UseMapToCompletionOptions<Diagnosis>(store.state.remoteLists.completionItems, 'reference', 'description', 'reference'))
const completionRequest = computed(() => store.state.remoteLists.completionRequest)

watch(() => props.showCopyActions, () => {
  if (!props.showCopyActions && isCopyAction.value) {
    actionAction.value = AutomationActionEnum.ADD_DP
  }
})

function selectItem(diag: Diagnosis) {
  actionValue.value = diag.reference
  emit('save-action', {
    action: actionAction.value,
    value: actionValue.value,
  })
  showCompletion.value = false
}
function openCompletion() {
  showCompletion.value = true
}
function closeCompletion() {
  showCompletion.value = false
}

function addCopyAction() {
  emit('save-action', {
    action: actionAction.value,
    value: '',
  })
}

function fetchCompletionItems(search: string, signal: AbortSignal) {
  store.dispatch('remoteLists/getCompletionItems', {
    url: '/api/stay-search-criteria/completion',
    urlParams: {
      type: 'diagnosis',
      search,
    },
    signal,
  })
}

function clearCompletionItems() {
  store.commit('remoteLists/clearCompletionItems')
}

useAutoCompletionDebouncedWatcher(typedInput, completionItems, fetchCompletionItems, clearCompletionItems)
</script>

<template>
  <div class="row">
    <div class="col-3 fw-bold text-end">
      <select
        v-model="actionAction"
        class="form-select"
      >
        <option
          v-for="action in availableActions"
          :key="action"
          :value="action"
        >
          {{ actionLabels[action] }}
        </option>
      </select>
    </div>
    <div class="col-6">
      <template v-if="isCopyAction">
        <button
          class="btn btn-primary"
          type="button"
          @click="addCopyAction"
        >
          {{ t('add') }}
        </button>
      </template>
      <template v-else>
        <div class="input-group">
          <input
            v-model="typedInput"
            v-on-click-outside="closeCompletion"
            class="form-control"
            @click="openCompletion"
            @keydown.esc="closeCompletion"
          >
        </div>
        <!-- @vue-generic {Diagnosis} -->
        <input-auto-completion
          v-show="showCompletion && typedInput !== ''"
          :completion-options="completionItems"
          :is-loading="completionRequest.fetching"
          @select-option="selectItem"
        />
      </template>
    </div>
  </div>
</template>
